import * as React from "react";
import { useSelector } from "react-redux";

import { tenantIdSelector } from "@/react/features/customer/customerSlice";
import { useErrorNotification } from "@/react/notifications";
import { useTable } from "@/react/components/table/useTable";
import TableComponent from "@/react/components/table/TableComponent";
import { useGetQueuesByMediaTypeQuery } from "@/react/features/vcc/common/api/queues";
import MediaTypes from "@/react/features/vcc/types/mediaTypes";

import inboundPhoneColumnDefinition from "./inboundPhoneColumnDefinition";
import schema from "./schema";
import useInboundQueuesMutations from "./useInboundQueuesMutations";
import { useGetTenantAudioFilesQuery } from "@/react/services/api/tenantApi";
import { useEffect, useMemo } from "react";
import { withAudioFileName } from "./audioFileTransforms";

const COLUMN_DEFINITION = inboundPhoneColumnDefinition();
const KEY_COLUMN_ID = schema.fields.id.spec.key;

const InboundPhoneQueues = () => {
  const tenantName = useSelector(tenantIdSelector);
  const {
    data: initialQueues,
    isFetching: isFetchingQueues,
    error: queuesError,
    refetch: refetchQueues,
  } = useGetQueuesByMediaTypeQuery({
    tenantName,
    mediaTypes: [MediaTypes.INBOUND_PHONE],
  });
  const {
    data: audioFiles,
    isFetching: isFetchingAudioFiles,
    error: audioFilesError,
    refetch: refetchAudioFiles,
  } = useGetTenantAudioFilesQuery({
    tenantName,
  });

  useEffect(() => {
    refetchQueues();
    refetchAudioFiles();
  }, [tenantName]);

  useErrorNotification(queuesError);
  useErrorNotification(audioFilesError);

  const tableData = useMemo(
    () => withAudioFileName({ audioFiles, inboundQueues: initialQueues }),
    [audioFiles, initialQueues]
  );

  const { table, filters, state, mutations } = useTable(
    tableData,
    COLUMN_DEFINITION,
    schema,
    KEY_COLUMN_ID
  );
  const { onSave, onCancel, isMutationOngoing, filteredMutations, isDirty } =
    useInboundQueuesMutations(tenantName, mutations);

  return (
    <TableComponent
      title="Inbound Phone Queues"
      schema={schema}
      isLoading={isFetchingQueues || isFetchingAudioFiles || isMutationOngoing}
      onRefresh={refetchQueues}
      table={table}
      filters={filters}
      mutations={filteredMutations}
      onSave={onSave}
      onCancel={onCancel}
      isDirty={isDirty}
      state={state}
    />
  );
};

export default InboundPhoneQueues;
